/*  MUI  */
import { Close, OpenInNew, Warning } from '@mui/icons-material';
import { Box, Chip, Grid, Typography } from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import { RunReportLink } from 'application/Shared/Components/Reports/RunReportLink';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { Helpers } from 'application/Shared/Helpers';

/*  Hooks  */
import { useWindowSize } from 'hooks/windowSize';

/*  React  */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TextTruncate from 'react-text-truncate';

/*  Constants  */
const { isEmpty } = Helpers;

const BreachContext = React.createContext({ breach: null });

export const Breach = ({ children, data }) => {
    const breachMemo = useMemo(() => ({ breach: data }), [data]);

    return (
        <BreachContext.Provider value={breachMemo}>
            {children}
        </BreachContext.Provider>
    );
};

export const BreachDescription = ({
    className,
    lines,
    noTruncate,
    includeSteps
}) => {
    const { breach } = useContext(BreachContext);
    const { description, id } = breach;

    const { windowSize } = useWindowSize();

    if (noTruncate) {
        return (
            <>
                <span style={{ fontWeight: 300 }}>{description}</span>
                {includeSteps && (
                    <Box mt="16px">
                        <BreachSteps />
                    </Box>
                )}
            </>
        );
    }

    return (
        <>
            <DarkTooltip arrow title={<div>{description}</div>}>
                <Box>
                    <TextTruncate
                        key={`breachDescription_${id}_${windowSize}`}
                        className={className}
                        element="span"
                        line={lines}
                        truncateText="…"
                        text={description}
                        style={{ fontWeight: 300 }}
                    />
                </Box>
            </DarkTooltip>
            {includeSteps && (
                <Box mt="16px">
                    <BreachSteps />
                </Box>
            )}
        </>
    );
};

export const BreachDateAnnounced = () => {
    const { breach } = useContext(BreachContext);

    return (
        <Typography variant="span" fontWeight={600}>
            <DateFormat date={breach?.dateAnnounced} noTimeString />
        </Typography>
    );
};

export const BreachNistId = () => {
    const { breach } = useContext(BreachContext);

    if (breach?.nistId !== 'N/A') {
        return (
            <a
                className="link underline"
                target="_blank"
                href={`https://nvd.nist.gov/vuln/detail/${breach.nistId}`}
                rel="noopener noreferrer nofollow"
                style={{ cursor: 'pointer' }}
            >
                {breach.nistId}
                <OpenInNew fontSize="small" sx={{ color: 'button.primary' }} />
            </a>
        );
    }
    return <span>{breach?.nistId}</span>;
};

export const BreachResources = ({ linkHeight, noTitle }) => {
    const { breach } = useContext(BreachContext);
    const { id, resources } = breach;
    const { t } = useTranslation();

    const renderSource = (source) => {
        const { url, type } = source;

        return (
            <Box
                key={`source_${id}_${source.type}`}
                style={{ lineHeight: linkHeight || '24px' }}
            >
                <DarkTooltip arrow placement="left" title={url}>
                    <a
                        className="link underline"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        style={{ cursor: 'pointer' }}
                    >
                        {type}
                    </a>
                </DarkTooltip>
            </Box>
        );
    };

    if (isEmpty(resources)) {
        return null;
    }

    const resourceText = [];
    resources.forEach((resource) => {
        const { type, url } = resource;

        let typeStr = '';
        Object.values(type).forEach((value) => {
            typeStr += `${value.trim()}, `;
        });
        typeStr = typeStr.substring(0, typeStr.length - 2);

        resourceText.push({
            text: `${typeStr}: ${url}`,
            type: typeStr,
            url
        });
    });

    return (
        <Box data-trackid="breachResources">
            {!noTitle && (
                <Typography
                    variant="body2"
                    style={{ fontWeight: 600, marginBottom: '4px' }}
                >
                    {t('Common.sources')}
                </Typography>
            )}
            {resourceText.map((source) => renderSource(source))}
        </Box>
    );
};

const renderStep = ({ step, i, id }) => (
    <Grid item xs={12} key={`step_${i}_${id}`} style={{ fontWeight: 300 }}>
        {step}
    </Grid>
);

export const BreachSteps = () => {
    const { breach } = useContext(BreachContext);
    const { id, steps } = breach;
    const { t } = useTranslation();

    if (isEmpty(steps)) {
        return null;
    }

    return (
        <Box data-trackid="breachSteps">
            <DarkTooltip
                arrow
                placement="left"
                title={
                    <div>{t('VendorIncident.suggested_actions_tooltip')}</div>
                }
            >
                <Typography fontWeight={600}>
                    {t('Common.suggested_actions')}
                </Typography>
            </DarkTooltip>
            <Grid container spacing={1}>
                {steps.map((step, i) => renderStep({ i, id, step }))}
            </Grid>
        </Box>
    );
};

export const BreachScore = () => {
    const { breach } = useContext(BreachContext);

    let { severityScore } = breach;
    severityScore = parseFloat(severityScore).toFixed(1);

    let color = 'risk.textColor.undefined';
    let borderColor = 'risk.borderColor.undefined';
    let backgroundColor = 'risk.backgroundColor.undefined';

    if (severityScore > 0) {
        color = 'risk.textColor.safe';
        borderColor = 'risk.borderColor.safe';
        backgroundColor = 'risk.backgroundColor.safe';
    }

    if (severityScore > 3) {
        color = 'risk.textColor.low';
        borderColor = 'risk.borderColor.low';
        backgroundColor = 'risk.backgroundColor.low';
    }

    if (severityScore > 5) {
        color = 'risk.textColor.medium';
        borderColor = 'risk.borderColor.medium';
        backgroundColor = 'risk.backgroundColor.medium';
    }

    if (severityScore > 8) {
        color = 'risk.textColor.high';
        borderColor = 'risk.borderColor.high';
        backgroundColor = 'risk.backgroundColor.high';
    }

    if (severityScore > 9) {
        color = 'risk.textColor.critical';
        borderColor = 'risk.borderColor.critical';
        backgroundColor = 'risk.backgroundColor.critical';
    }

    return (
        <Chip
            label={severityScore}
            sx={{
                backgroundColor,
                border: '1px solid',
                borderColor,
                color,
                fontWeight: 600
            }}
        />
    );
};

export const BreachClientsImpacted = ({ onlyThisTenant }) => {
    const { breach } = useContext(BreachContext);
    const { t } = useTranslation();

    let color = 'text.primary';

    let clientsImpacted = 0;

    if (breach?.clientsImpacted > 0) {
        clientsImpacted = breach.clientsImpacted;
    }

    if (clientsImpacted > 0) {
        color = 'breach.textColor.critical';
    }

    if (onlyThisTenant) {
        return (
            <span>
                {parseInt(clientsImpacted, 10) === 1 ? (
                    <Chip
                        icon={
                            <Warning
                                color="risk.icon.critical"
                                sx={{
                                    '&.MuiChip-icon': {
                                        color: 'risk.icon.critical'
                                    }
                                }}
                            />
                        }
                        sx={{
                            backgroundColor: 'breach.backgroundColor.critical',
                            border: '1px solid',
                            borderColor: 'breach.borderColor.critical',
                            color: 'breach.textColor.default'
                        }}
                        size="small"
                        label={t('VendorIncident.potential_exposure')}
                    />
                ) : (
                    <Chip
                        icon={
                            <Close
                                color="risk.icon.low"
                                sx={{
                                    '&.MuiChip-icon': {
                                        color: 'risk.icon.low'
                                    }
                                }}
                            />
                        }
                        sx={{
                            backgroundColor: 'breach.backgroundColor.safe',
                            border: '1px solid',
                            borderColor: 'breach.borderColor.safe',
                            color: 'breach.textColor.default'
                        }}
                        size="small"
                        label={t('VendorIncident.none_detected')}
                    />
                )}
            </span>
        );
    }

    return (
        <Typography
            dangerouslySetInnerHTML={{
                __html: t('VendorIncident.numberClientsImpacted', {
                    count: clientsImpacted
                })
            }}
            fontWeight={600}
            color={color}
        />
    );
};

export const BreachDetail = ({ hideClientsImpactedLine }) => {
    const { breach } = useContext(BreachContext);
    const { resources, steps } = breach;

    const hasResources = !isEmpty(resources);
    const hasSteps = !isEmpty(steps);

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                data-trackid="breachDetail"
            >
                <Box>
                    <Box display="flex" style={{ flexFlow: 'wrap' }}>
                        <RunReportLink
                            breachSelection={[breach.id]}
                            period="daily"
                            reportType="partnerBreachImpact"
                            run
                        >
                            <ApplicationNameLinkCell nameClassname="link underline bold" />
                        </RunReportLink>
                        &nbsp;(
                        <BreachDateAnnounced />)
                    </Box>
                </Box>
                {!hideClientsImpactedLine && <BreachClientsImpacted />}
                <BreachScore />
            </Box>
            <Box mt="8px" mb="16px">
                <BreachDescription lines={2} />
            </Box>
            <Grid container>
                {hasResources && (
                    <Grid item xs={hasSteps ? 4 : 12}>
                        <BreachResources />
                    </Grid>
                )}
                {hasSteps && (
                    <Grid item xs={hasResources ? 8 : 12}>
                        <BreachSteps />
                    </Grid>
                )}
            </Grid>
        </>
    );
};
